import { createReducer, on } from '@ngrx/store';
import { DefinedError } from 'ajv';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';

import { apiResponse, postApiRequest } from '../api/api.actions';
import { isApi } from '../api/api.utilities';
import { GetApplicationConfigResponse } from '../api/responses/get-application-config';
import { clearApplicationConfigAction, updateAccessoIdLoginConfig } from './application-config.actions';
import { ApplicationConfigs, ApplicationConfigState, initialState } from './application-config.state';
import { ajv, valid } from './application-config.utils';

const { PAYMENT_PORTAL_LOGIN, UNIFIED_GUEST_LOGIN, GET_APPLICATION_CONFIG } = ApiRequestType;

const reducer = createReducer(
	initialState,
	on(clearApplicationConfigAction, (state) => initialState),
	on(postApiRequest, (state, action) => {
		if (!isApi(action, GET_APPLICATION_CONFIG)) {
			return state;
		}

		return {
			...state,
			loaded: false,
			loading: true,
			errorLoading: false,
		};
	}),
	on(apiResponse, (state, action) => {
		if (
			!isApi(action, GET_APPLICATION_CONFIG) &&
			!isApi(action, PAYMENT_PORTAL_LOGIN) &&
			!isApi(action, UNIFIED_GUEST_LOGIN)
		) {
			return state;
		}

		const statusOk = action.isOk;

		if (isApi(action, PAYMENT_PORTAL_LOGIN) || isApi(action, UNIFIED_GUEST_LOGIN)) {
			if (!statusOk) {
				return state;
			}

			return {
				...state,
				configs: {
					...state.configs,
					supportSecondPayment: action.response.support_secondary_payment === '1',
				},
			};
		}

		const newState = {
			...state,
			loaded: true,
			loading: false,
			errorLoading: !statusOk,
			configs: { ...state.configs },
		};

		if (statusOk) {
			return { ...newState, configs: { ...parseSettings(action.response as GetApplicationConfigResponse) } };
		} else {
			const { error_code, error_msg } = action.response;
			return { ...newState, error_code, error_msg };
		}
	}),
	on(updateAccessoIdLoginConfig, (state, action) => {
		return {
			...state,
			configs: {
				...state.configs,
				accesso_id: {
					...state.configs.accesso_id,
					show_legacy_sign_in: action.show_legacy_sign_in,
				},
			},
		};
	})
);

/**
 * Our main reducer for application config feature
 * @param state The current state
 * @param action The action being triggered
 */
export function applicationConfigReducer(state, action): ApplicationConfigState {
	return reducer(state, action);
}

/**
 * Parses the application config settings to actual types
 * @param response The configs
 * @returns The parsed application configs
 */
function parseSettings(response: GetApplicationConfigResponse): ApplicationConfigs {
	const { SETTINGS } = response;
	return SETTINGS.reduce((accum: ApplicationConfigs, configs: ApplicationConfigs) => {
		Object.keys(configs).forEach((key: string) => {
			let data;

			try {
				data = JSON.parse(configs[key]);
			} catch (ignore) {
				data = configs[key];
			}

			if (valid(key, data)) {
				accum[key] = data;
			} else {
				for (const err of ajv.errors as DefinedError[]) {
					console.warn(`App config error: ${key}`);
					console.warn(err);
				}
			}
		});

		return accum;
	}, {});
}
